import { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faPhone,
  faEnvelope,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";

/* ── Helpers ─────────────────────────────────────────────────────────────── */

const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h${m}`;
};

/* ── Component ───────────────────────────────────────────────────────────── */

const CatalogueWidget = ({ widgetId }) => {
  const [widget, setWidget] = useState(null);
  const [isError, setIsError] = useState(false);
  const [selectedPrestations, setSelectedPrestations] = useState({});
  const [quantities, setQuantities] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [clientInfo, setClientInfo] = useState({});

  /* ── Fetch ── */
  useEffect(() => {
    const fetchWidget = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/widgets/catalogue/getOneWidget/${widgetId}`
        );
        if (response.status === 200) {
          const w = response.data.widget;
          if (!w.isOnline) {
            setIsError(true);
          } else {
            setWidget(w);
          }
        }
      } catch (error) {
        setIsError(true);
      }
    };
    fetchWidget();
  }, [widgetId]);

  /* ── Sorted data ── */
  const sortedCategories = widget
    ? [...(widget.categories || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [];

  const getSortedPrestations = (category) =>
    [...(category.prestations || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  /* ── Computed totals ── */
  const totalPrice = sortedCategories.reduce((catAcc, cat, catIdx) => {
    const sortedPrestas = getSortedPrestations(cat);
    return (
      catAcc +
      sortedPrestas.reduce((prestaAcc, presta, prestaIdx) => {
        if (!selectedPrestations[catIdx]?.[prestaIdx]) return prestaAcc;
        const qty = quantities[catIdx]?.[prestaIdx] || 1;
        return prestaAcc + (presta.price || 0) * qty;
      }, 0)
    );
  }, 0);

  const totalDuration = sortedCategories.reduce((catAcc, cat, catIdx) => {
    const sortedPrestas = getSortedPrestations(cat);
    return (
      catAcc +
      sortedPrestas.reduce((prestaAcc, presta, prestaIdx) => {
        if (!selectedPrestations[catIdx]?.[prestaIdx]) return prestaAcc;
        const qty = quantities[catIdx]?.[prestaIdx] || 1;
        return prestaAcc + (presta.duration || 0) * qty;
      }, 0)
    );
  }, 0);

  const selectionCount = Object.values(selectedPrestations).reduce((acc, catMap) => {
    return acc + Object.values(catMap).filter(Boolean).length;
  }, 0);

  /* ── Handlers ── */
  const handlePrestaToggle = (catIdx, prestaIdx) => {
    setSelectedPrestations((prev) => {
      const catMap = prev[catIdx] || {};
      const next = { ...prev, [catIdx]: { ...catMap, [prestaIdx]: !catMap[prestaIdx] } };
      if (catMap[prestaIdx]) {
        setQuantities((q) => {
          const catQ = q[catIdx] || {};
          return { ...q, [catIdx]: { ...catQ, [prestaIdx]: 1 } };
        });
      }
      return next;
    });
  };

  const handleQuantityChange = (catIdx, prestaIdx, delta) => {
    setQuantities((prev) => {
      const catMap = prev[catIdx] || {};
      const current = catMap[prestaIdx] || 1;
      const next = Math.max(1, current + delta);
      return { ...prev, [catIdx]: { ...catMap, [prestaIdx]: next } };
    });
  };

  const handleInputChange = (e) => {
    setClientInfo((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    axios
      .post(
        `${process.env.REACT_APP_API_URL}/api/widgets/catalogue/recordClick/${widgetId}`
      )
      .catch(() => {});

    const selectedLines = [];
    sortedCategories.forEach((cat, catIdx) => {
      getSortedPrestations(cat).forEach((presta, prestaIdx) => {
        if (!selectedPrestations[catIdx]?.[prestaIdx]) return;
        const qty = quantities[catIdx]?.[prestaIdx] || 1;
        if (widget.priceActive) {
          const lineTotal = (presta.price || 0) * qty;
          const qtyLabel = qty > 1 ? ` x${qty}` : "";
          selectedLines.push(`- ${presta.name}${qtyLabel} : ${lineTotal.toFixed(2)}€`);
        } else {
          selectedLines.push(`- ${presta.name}`);
        }
      });
    });

    const recapSection = widget.priceActive
      ? `Récapitulatif :\n${selectedLines.join("\n")}\n\nTotal : ${totalPrice.toFixed(2)}€${totalDuration > 0 ? `\nDurée estimée : ${formatDuration(totalDuration)}` : ""}`
      : `Prestations sélectionnées :\n${selectedLines.join("\n")}`;

    let emailBody = `Bonjour,\n\nJe souhaiterais obtenir un devis pour les prestations suivantes.\n\nNom : ${clientInfo.lastName || ""}\nPrénom : ${clientInfo.firstName || ""}\nTéléphone : ${clientInfo.phone || ""}\nEmail : ${clientInfo.email || ""}\n\n${recapSection}\n\nMessage :\n${clientInfo.message || "—"}\n\nCordialement,\n${clientInfo.firstName || ""} ${clientInfo.lastName || ""}`;

    if (emailBody.length > 1800) {
      emailBody = emailBody.substring(0, 1800) + "\n\nRécapitulatif complet disponible dans le widget.";
    }

    window.location.href = `mailto:${widget.contactMail}?subject=Devis ${widget.title || "prestations"}&body=${encodeURIComponent(emailBody)}`;
  };

  /* ── Error state ── */
  if (isError) {
    return (
      <div className="flex items-center justify-center h-screen w-screen bg-neutral-950 text-white">
        <div className="text-center p-8">
          <p className="text-xl font-semibold mb-2">Simulateur désactivé</p>
          <p className="text-sm text-neutral-400">
            Contactez{" "}
            <a href="https://local.fr" className="underline hover:text-white">
              Local.fr
            </a>{" "}
            pour plus d'informations.
          </p>
        </div>
      </div>
    );
  }

  /* ── Loading state ── */
  if (!widget) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-5 h-5 border-2 border-neutral-300 border-t-neutral-800 rounded-full animate-spin" />
      </div>
    );
  }

  const bg = widget.backgroundColor || "#1a1a1a";
  const btn = widget.btnColor || "#1a1a1a";
  const lbl = widget.labelColor || "#ffffff";
  const brd = widget.borderColor || "transparent";
  const hasBorder = brd !== "transparent";

  const inputCls =
    "w-full px-3 py-2.5 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-neutral-900 placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:bg-white transition-all duration-200";

  const widgetStyles = `
    .cw-presta {
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .cw-presta:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 14px rgba(0,0,0,0.07);
    }
    .cw-presta:active {
      transform: translateY(0) scale(0.99);
    }
    .cw-presta.cw-active {
      box-shadow: 0 4px 16px rgba(0,0,0,0.12);
    }
    .cw-presta.cw-active:hover {
      box-shadow: 0 6px 22px rgba(0,0,0,0.18);
      transform: translateY(-2px);
    }
    .cw-btn {
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }
    .cw-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(0,0,0,0.15);
    }
    .cw-btn:active {
      transform: translateY(0) scale(0.98);
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    .cw-estimation {
      box-shadow: 0 2px 12px rgba(0,0,0,0.06);
    }
    .cw-phone-link {
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .cw-phone-link:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.06);
    }
    .cw-phone-link:active {
      transform: translateY(0);
    }
    .cw-phone-header {
      transition: all 0.15s ease;
    }
    .cw-phone-header:hover {
      transform: scale(1.08);
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    }
    .cw-phone-header:active {
      transform: scale(0.95);
    }
    .cw-qty-btn {
      transition: all 0.15s ease;
    }
    .cw-qty-btn:hover {
      transform: scale(1.12);
    }
    .cw-qty-btn:active {
      transform: scale(0.9);
    }
    .cw-back {
      transition: all 0.15s ease;
    }
    .cw-back:hover {
      transform: translateX(-2px);
    }
    .cw-checkbox {
      transition: all 0.15s ease;
    }
    .cw-presta:hover .cw-checkbox:not(.cw-checked) {
      border-color: #999;
      background: rgba(0,0,0,0.03);
    }
  `;

  /* ── Render ── */
  return (
    <>
      <style>{widgetStyles}</style>
      <div
        className="flex flex-col w-full bg-white sm:rounded-2xl overflow-hidden"
        style={{
          boxShadow: "0 8px 40px rgba(0,0,0,0.10)",
          border: hasBorder ? `2px solid ${brd}` : "1px solid #e5e5e5",
        }}
      >
        {/* ── Header ── */}
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{
            backgroundColor: bg,
            borderBottom: hasBorder ? `2px solid ${brd}` : "none",
          }}
        >
          <div className="min-w-0">
            <p className="text-[15px] font-bold truncate" style={{ color: lbl }}>
              {widget.title}
            </p>
            {widget.subtitle && (
              <p className="text-[11px] truncate mt-0.5" style={{ color: lbl, opacity: 0.6 }}>
                {widget.subtitle}
              </p>
            )}
          </div>
          {widget.contactPhone && !showForm && (
            <a
              href={`tel:${widget.contactPhone}`}
              className="cw-phone-header shrink-0 ml-3 w-9 h-9 rounded-full flex items-center justify-center text-xs"
              style={{ backgroundColor: `${lbl}15`, color: lbl }}
            >
              <FontAwesomeIcon icon={faPhone} />
            </a>
          )}
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto styled-scrollbar relative">
          {!showForm ? (
            /* ══ Step 1: Selection ══ */
            <div className="flex flex-col">

              {/* Estimation block — sticky top */}
              {widget.priceActive && selectionCount > 0 && (
                <div
                  className="sticky top-0 z-10 px-5 pt-5 pb-3"
                  style={{ backgroundColor: "white" }}
                >
                  <div
                    className="cw-estimation rounded-2xl p-4"
                    style={{ backgroundColor: bg }}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p
                          className="text-[15px] font-extrabold"
                          style={{ color: lbl }}
                        >
                          Estimation
                        </p>
                        <p
                          className="text-[13px] font-bold mt-1"
                          style={{ color: lbl }}
                        >
                          {totalDuration > 0
                            ? `~${formatDuration(totalDuration)} · `
                            : ""}
                          {selectionCount} prestation{selectionCount > 1 ? "s" : ""}
                        </p>
                      </div>
                      <p
                        className="text-2xl sm:text-3xl font-extrabold tracking-tight leading-none"
                        style={{ color: lbl }}
                      >
                        {totalPrice.toFixed(0)}
                        <span className="text-sm font-medium ml-0.5" style={{ opacity: 0.7 }}>€</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Categories + Prestations */}
              <div className="px-5 pt-3 pb-0 flex flex-col gap-5">
              {sortedCategories.map((cat, catIdx) => {
                const sortedPrestas = getSortedPrestations(cat);
                return (
                  <div key={catIdx}>
                    <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-2.5 pl-1">
                      {cat.name}
                    </p>
                    <div className="flex flex-col gap-2">
                      {sortedPrestas.map((presta, prestaIdx) => {
                        const active = !!selectedPrestations[catIdx]?.[prestaIdx];
                        const qty = quantities[catIdx]?.[prestaIdx] || 1;
                        const showQty = presta.quantityEnabled && active;

                        return (
                          <div
                            key={prestaIdx}
                            role="button"
                            tabIndex={0}
                            onClick={() => handlePrestaToggle(catIdx, prestaIdx)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handlePrestaToggle(catIdx, prestaIdx);
                              }
                            }}
                            className={`cw-presta flex items-center justify-between px-4 py-3.5 rounded-2xl cursor-pointer select-none ${
                              active ? "cw-active" : "text-neutral-800"
                            }`}
                            style={
                              active
                                ? { backgroundColor: btn, color: lbl }
                                : { backgroundColor: "#f7f7f8" }
                            }
                          >
                            {/* Left: checkbox + name/desc */}
                            <div className="flex items-center gap-3 min-w-0">
                              <div
                                className={`cw-checkbox w-[20px] h-[20px] rounded-lg flex items-center justify-center shrink-0 ${
                                  active ? "cw-checked" : "border-2 border-neutral-300"
                                }`}
                                style={active ? { backgroundColor: `${lbl}30` } : {}}
                              >
                                {active && (
                                  <svg
                                    className="w-2.5 h-2.5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={3.5}
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-[13px] font-semibold leading-tight truncate">
                                  {presta.name}
                                </p>
                                {presta.description && (
                                  <p
                                    className="text-[11px] leading-tight mt-0.5 truncate"
                                    style={{
                                      color: active ? lbl : "#999",
                                      opacity: active ? 0.5 : 1,
                                    }}
                                  >
                                    {presta.description}
                                  </p>
                                )}
                              </div>
                            </div>

                            {/* Right: duration + quantity or price */}
                            <div
                              className="flex items-center gap-2 shrink-0 ml-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {presta.duration > 0 && (
                                <span
                                  className="text-[10px] font-medium"
                                  style={{
                                    color: active ? lbl : "#bbb",
                                    opacity: active ? 0.4 : 1,
                                  }}
                                >
                                  {formatDuration(presta.duration)}
                                </span>
                              )}

                              {showQty ? (
                                <>
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      type="button"
                                      className="cw-qty-btn w-7 h-7 rounded-lg flex items-center justify-center text-sm font-semibold"
                                      style={{ backgroundColor: `${lbl}15`, color: lbl }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleQuantityChange(catIdx, prestaIdx, -1);
                                      }}
                                    >
                                      −
                                    </button>
                                    <span className="text-sm font-bold min-w-[18px] text-center" style={{ color: lbl }}>
                                      {qty}
                                    </span>
                                    <button
                                      type="button"
                                      className="cw-qty-btn w-7 h-7 rounded-lg flex items-center justify-center text-sm font-semibold"
                                      style={{ backgroundColor: `${lbl}15`, color: lbl }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleQuantityChange(catIdx, prestaIdx, 1);
                                      }}
                                    >
                                      +
                                    </button>
                                  </div>
                                  {widget.priceActive && (
                                    <span
                                      className="text-[11px] font-bold px-2.5 py-1 rounded-lg"
                                      style={{ backgroundColor: `${lbl}15`, color: lbl }}
                                    >
                                      {presta.price}€{qty > 1 ? "/u" : ""}
                                    </span>
                                  )}
                                </>
                              ) : (
                                widget.priceActive && (
                                  <span
                                    className="text-[11px] font-bold px-2.5 py-1 rounded-lg"
                                    style={
                                      active
                                        ? { backgroundColor: `${lbl}15`, color: lbl }
                                        : { backgroundColor: "#eaeaec", color: "#555" }
                                    }
                                  >
                                    {presta.price}€
                                  </span>
                                )
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              </div>

              {/* CTA — sticky bottom */}
              <div
                className="sticky bottom-0 z-10 px-5 pt-3 pb-5"
                style={{ backgroundColor: "white" }}
              >
                <button
                  type="button"
                  disabled={selectionCount === 0}
                  className="cw-btn w-full py-3.5 rounded-2xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                  style={{
                    backgroundColor: btn,
                    color: lbl,
                    border: hasBorder ? `2px solid ${brd}` : "none",
                  }}
                  onClick={() => setShowForm(true)}
                >
                  Demander un devis
                  <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                </button>
              </div>
            </div>
          ) : (
            /* ══ Step 2: Contact form ══ */
            <form onSubmit={handleFormSubmit} className="flex flex-col">

              {/* Sticky recap header */}
              <div className="sticky top-0 z-10 bg-white px-5 pt-5 pb-3">
                {/* Back button */}
                <button
                  type="button"
                  className="cw-back flex items-center gap-1.5 text-xs font-semibold text-neutral-500 hover:text-neutral-900 mb-3"
                  onClick={() => setShowForm(false)}
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                  Retour
                </button>

                {/* Recap bar */}
                {widget.priceActive && (
                  <div
                    className="rounded-2xl p-4 flex items-center justify-between"
                    style={{ backgroundColor: bg, boxShadow: "0 4px 14px rgba(0,0,0,0.08)" }}
                  >
                    <div>
                      <p
                        className="text-sm font-extrabold"
                        style={{ color: lbl }}
                      >
                        Votre devis
                      </p>
                      <p
                        className="text-[12px] font-bold mt-0.5"
                        style={{ color: lbl, opacity: 0.55 }}
                      >
                        {selectionCount} prestation{selectionCount > 1 ? "s" : ""}
                        {totalDuration > 0 ? ` · ~${formatDuration(totalDuration)}` : ""}
                      </p>
                    </div>
                    <p
                      className="text-2xl font-extrabold tracking-tight"
                      style={{ color: lbl }}
                    >
                      {totalPrice.toFixed(0)}
                      <span className="text-sm font-medium ml-0.5" style={{ opacity: 0.7 }}>€</span>
                    </p>
                  </div>
                )}
              </div>

              <div className="px-5 pb-5 flex flex-col gap-4">

              {/* Detailed recap */}
              <div className="bg-white rounded-2xl p-4" style={{ boxShadow: "0 4px 14px rgba(0,0,0,0.08)" }}>
                <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-2.5">
                  Récapitulatif
                </p>
                <div className="flex flex-col">
                  {sortedCategories.map((cat, catIdx) =>
                    getSortedPrestations(cat).map((presta, prestaIdx) => {
                      if (!selectedPrestations[catIdx]?.[prestaIdx]) return null;
                      const qty = quantities[catIdx]?.[prestaIdx] || 1;
                      const lineTotal = (presta.price || 0) * qty;
                      return (
                        <div
                          key={`${catIdx}-${prestaIdx}`}
                          className="flex items-center justify-between text-sm text-neutral-600 py-1.5"
                        >
                          <span>
                            {presta.name}
                            {qty > 1 && (
                              <span className="text-neutral-400 text-xs ml-1">x{qty}</span>
                            )}
                          </span>
                          {widget.priceActive && (
                            <span className="font-semibold text-neutral-800 ml-4">
                              {lineTotal.toFixed(2)}€
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}

                  {/* Total line */}
                  {widget.priceActive && (
                    <div className="flex items-center justify-between pt-2.5 mt-1.5 border-t border-neutral-200 text-sm font-bold text-neutral-900">
                      <span>Total</span>
                      <span>{totalPrice.toFixed(2)}€</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact form */}
              <div className="bg-white rounded-2xl p-4 flex flex-col gap-3" style={{ boxShadow: "0 4px 14px rgba(0,0,0,0.08)" }}>
                <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">
                  Vos coordonnées
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <div>
                    <label className="text-[11px] font-semibold text-neutral-600 mb-1 block">
                      Nom *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Dupont"
                      className={inputCls}
                      value={clientInfo.lastName || ""}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-neutral-600 mb-1 block">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      placeholder="Jean"
                      className={inputCls}
                      value={clientInfo.firstName || ""}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <div>
                    <label className="text-[11px] font-semibold text-neutral-600 mb-1 block">
                      Téléphone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="06 12 34 56 78"
                      className={inputCls}
                      value={clientInfo.phone || ""}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-neutral-600 mb-1 block">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      placeholder="votre@email.com"
                      className={inputCls}
                      value={clientInfo.email || ""}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[11px] font-semibold text-neutral-600 mb-1 block">
                    Message
                  </label>
                  <textarea
                    name="message"
                    placeholder="Précisez votre demande..."
                    className={`${inputCls} min-h-[80px] resize-none`}
                    value={clientInfo.message || ""}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              </div>

              {/* Sticky submit bottom */}
              <div className="sticky bottom-0 z-10 bg-white px-5 pt-3 pb-5 flex flex-col gap-2.5">
                <button
                  type="submit"
                  className="cw-btn w-full py-3.5 rounded-2xl text-sm font-bold flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: btn,
                    color: lbl,
                    border: hasBorder ? `2px solid ${brd}` : "none",
                  }}
                >
                  <FontAwesomeIcon icon={faEnvelope} />
                  Envoyer par email
                </button>

                {/* Phone link */}
                {widget.contactPhone && (
                  <a
                    href={`tel:${widget.contactPhone}`}
                    className="cw-phone-link flex items-center justify-center gap-2 py-3 rounded-2xl text-sm font-semibold text-neutral-600"
                    style={{
                      border: `1.5px solid ${hasBorder ? brd : "#e5e5e5"}`,
                    }}
                  >
                    <FontAwesomeIcon icon={faPhone} className="text-xs" />
                    {widget.contactPhone}
                  </a>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default CatalogueWidget;
